"use client";

import { useState } from "react";
import {
  Checkbox,
  DataTable,
  Notice,
  PillGroup,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  CARD,
  HINT,
  LABEL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  ResultCard,
  ScriptTextArea,
} from "@/components/tools/transliteration-more/shared";
import { t } from "@/lib/i18n/interpolate";
import {
  cyrillicToLatin,
  latinToCyrillic,
  type SerbianOptions,
  type SerbianScheme,
  serbianLetterRows,
} from "@/lib/tool-kit/transliteration-more/serbian";

/** English strings. A translated content file overrides these through `ui`. */
export const SERBIAN_CYRILLIC_LATIN_CONVERTER_STRINGS = {
  direction: "Direction",
  toLatin: "Cyrillic → Latin",
  toCyrillic: "Latin → Cyrillic",
  language: "Language",
  languages: { sr: "Serbian", mk: "Macedonian" },
  macedonianScheme: "Macedonian Latin",
  macedonianSchemes: {
    "mk-academy": "Academy — ǵ ḱ dz dž (Gaj-based)",
    "mk-digraph": "Digraphs — gj kj zh ch sh dj (passports)",
  },
  inputLabel: "Cyrillic text",
  inputHint:
    "Serbian or Macedonian. Letters of other Cyrillic alphabets (such as ы or щ) are left as they are.",
  latinInputLabel: "Latin text",
  latinInputHint:
    "Serbian Latin (Gaj's alphabet) or Macedonian Latin. The pairs lj, nj and dž are read as one letter each; write l+j, n+j or d+ž to keep two letters apart.",
  samples: {
    sr: "Ђорђе Ђурђевић је отишао у Љубљану, а Његош у Цетиње.",
    mk: "Скопје, Охрид, Ѓорѓи, Ќустендил, Жито, Ѕвезда, Џем",
    "sr-latin": "Đorđe Đurđević je otišao u Ljubljanu, a Njegoš u Cetinje.",
    "mk-academy": "Skopje, Ohrid, Ǵorǵi, Ḱustendil, Žito, Dzvezda, Džem",
    "mk-digraph": "Skopje, Ohrid, Gjorgji, Kjustendil, Zhito, Dzvezda, Djem",
  },
  sample: "Insert a sample",
  optionsLegend: "Options",
  djForDje: "Serbian: write and read đ as dj (Djoković)",
  resultsHeading: "Latin",
  cyrillicHeading: "Cyrillic",
  titles: {
    sr: "Gaj's Latin alphabet",
    "mk-academy": "Macedonian — Academy",
    "mk-digraph": "Macedonian — digraphs",
  },
  uses: {
    sr: "The Latin alphabet of Serbian, Croatian and Bosnian; the same letters as the UN and Library of Congress systems.",
    "mk-academy":
      "Gaj's alphabet adapted for Macedonian by the 1970 orthography: ǵ and ḱ replace đ and ć, and dz is added.",
    "mk-digraph":
      "No diacritics. The system used for passports and other official documents since 2008.",
  },
  cyrillicUse: "Cyrillic written from the {system} text above.",
  enterText: "Type or paste some text to convert.",
  unknownCyrillic:
    "Left unchanged because this alphabet has no value for them: {chars}.",
  unknownLatin:
    "Left unchanged because they are not letters of this alphabet: {chars}.",
  copy: "Copy",
  copied: "Copied!",
  tableHeading: "Letter by letter",
  letterColumn: "Cyrillic",
  serbianColumn: "Serbian Latin",
  academyColumn: "Macedonian (Academy)",
  digraphColumn: "Macedonian (digraphs)",
  tableNote:
    "A blank cell means the language does not use the letter. Serbian ђ and ћ have no Macedonian equivalent; Macedonian ѓ, ќ and ѕ have no Serbian one.",
} as const;

type Strings = typeof SERBIAN_CYRILLIC_LATIN_CONVERTER_STRINGS;
type Direction = "to-latin" | "to-cyrillic";
type Language = "sr" | "mk";
type MkScheme = "mk-academy" | "mk-digraph";

const MK_SCHEMES: readonly MkScheme[] = ["mk-academy", "mk-digraph"];

function LetterTable({ strings }: { strings: Strings }) {
  return (
    <section className={`${CARD} space-y-3`}>
      <h2 className={WIDGET_H2}>{strings.tableHeading}</h2>
      <DataTable
        head={[
          strings.letterColumn,
          strings.serbianColumn,
          strings.academyColumn,
          strings.digraphColumn,
        ]}
        rows={serbianLetterRows().map((row) => [
          row.cyrillic,
          row.serbian,
          row.macedonianAcademy,
          row.macedonianDigraph,
        ])}
      />
      <p className={HINT}>{strings.tableNote}</p>
    </section>
  );
}

function ToLatinResults({
  text,
  schemes,
  options,
  strings,
}: {
  text: string;
  schemes: readonly SerbianScheme[];
  options: SerbianOptions;
  strings: Strings;
}) {
  const results = schemes.map((scheme) => ({
    scheme,
    result: cyrillicToLatin(text, scheme, options),
  }));
  const unknown = [...new Set(results.flatMap((each) => each.result.unknown))];
  return (
    <section aria-live="polite" className="space-y-3">
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      {unknown.length > 0 ? (
        <Notice>
          {t(strings.unknownCyrillic, { chars: unknown.join(" ") })}
        </Notice>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        {results.map(({ scheme, result }) => (
          <ResultCard
            copiedLabel={strings.copied}
            copyLabel={strings.copy}
            key={scheme}
            lang={scheme === "sr" ? "sr-Latn" : "mk-Latn"}
            text={result.text}
            title={strings.titles[scheme]}
            use={strings.uses[scheme]}
          />
        ))}
      </div>
    </section>
  );
}

function ToCyrillicResults({
  text,
  scheme,
  options,
  strings,
}: {
  text: string;
  scheme: SerbianScheme;
  options: SerbianOptions;
  strings: Strings;
}) {
  const result = latinToCyrillic(text, scheme, options);
  return (
    <section aria-live="polite" className="space-y-3">
      <h2 className={WIDGET_H2}>{strings.cyrillicHeading}</h2>
      {result.unknown.length > 0 ? (
        <Notice>
          {t(strings.unknownLatin, { chars: result.unknown.join(" ") })}
        </Notice>
      ) : null}
      <ResultCard
        copiedLabel={strings.copied}
        copyLabel={strings.copy}
        lang={scheme === "sr" ? "sr" : "mk"}
        text={result.text}
        title={strings.titles[scheme]}
        use={t(strings.cyrillicUse, { system: strings.titles[scheme] })}
      />
    </section>
  );
}

export default function SerbianCyrillicLatinConverterWidget() {
  const strings = useToolUi(SERBIAN_CYRILLIC_LATIN_CONVERTER_STRINGS);
  const [direction, setDirection] = useState<Direction>("to-latin");
  const [language, setLanguage] = useState<Language>("sr");
  const [mkScheme, setMkScheme] = useState<MkScheme>("mk-academy");
  const [cyrillic, setCyrillic] = useState<string>(strings.samples.sr);
  const [latin, setLatin] = useState<string>(strings.samples["sr-latin"]);
  const [options, setOptions] = useState<SerbianOptions>({ djForDje: false });
  const forward = direction === "to-latin";
  const current = forward ? cyrillic : latin;
  const scheme: SerbianScheme = language === "sr" ? "sr" : mkScheme;
  const forwardSchemes: readonly SerbianScheme[] =
    language === "sr" ? ["sr"] : MK_SCHEMES;

  function changeLanguage(next: Language) {
    const isSample =
      cyrillic === strings.samples[language] || cyrillic.trim() === "";
    setLanguage(next);
    if (isSample) {
      setCyrillic(strings.samples[next]);
    }
    const latinSample = next === "sr" ? "sr-latin" : mkScheme;
    if (
      (Object.values(strings.samples) as string[]).includes(latin) ||
      latin.trim() === ""
    ) {
      setLatin(strings.samples[latinSample]);
    }
  }

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.direction}
        name="sr-direction"
        onChange={setDirection}
        options={[
          { value: "to-latin", label: strings.toLatin },
          { value: "to-cyrillic", label: strings.toCyrillic },
        ]}
        value={direction}
      />
      <PillGroup
        label={strings.language}
        name="sr-language"
        onChange={changeLanguage}
        options={(["sr", "mk"] as const).map((value) => ({
          value,
          label: strings.languages[value],
        }))}
        value={language}
      />
      {language === "mk" && !forward ? (
        <PillGroup
          label={strings.macedonianScheme}
          name="sr-mk-scheme"
          onChange={setMkScheme}
          options={MK_SCHEMES.map((value) => ({
            value,
            label: strings.macedonianSchemes[value],
          }))}
          value={mkScheme}
        />
      ) : null}
      {forward ? (
        <ScriptTextArea
          dir="ltr"
          hint={strings.inputHint}
          id="sr-input"
          label={strings.inputLabel}
          lang={language}
          onChange={setCyrillic}
          value={cyrillic}
        />
      ) : (
        <ScriptTextArea
          dir="ltr"
          hint={strings.latinInputHint}
          id="sr-latin-input"
          label={strings.latinInputLabel}
          lang={`${language}-Latn`}
          onChange={setLatin}
          value={latin}
        />
      )}
      <div className="flex flex-wrap gap-2">
        <button
          className={BUTTON_SMALL}
          onClick={() =>
            forward
              ? setCyrillic(strings.samples[language])
              : setLatin(
                  strings.samples[language === "sr" ? "sr-latin" : mkScheme]
                )
          }
          type="button"
        >
          {strings.sample}
        </button>
      </div>
      {language === "sr" ? (
        <fieldset className="space-y-3">
          <legend className={LABEL}>{strings.optionsLegend}</legend>
          <Checkbox
            checked={options.djForDje}
            label={strings.djForDje}
            onChange={(djForDje) => setOptions({ djForDje })}
          />
        </fieldset>
      ) : null}
      {current.trim() === "" ? <Notice>{strings.enterText}</Notice> : null}
      {current.trim() !== "" && forward ? (
        <ToLatinResults
          options={options}
          schemes={forwardSchemes}
          strings={strings}
          text={cyrillic}
        />
      ) : null}
      {current.trim() !== "" && !forward ? (
        <ToCyrillicResults
          options={options}
          scheme={scheme}
          strings={strings}
          text={latin}
        />
      ) : null}
      <LetterTable strings={strings} />
    </div>
  );
}
